/**
 * Devall Product Options — progressive enhancement + variant-backed pricing.
 *
 * Pricing is NATIVE: priced axes (fields with data-variant-axis) map to real
 * product variants. This resolves the selected combination to a variant, writes
 * its id into the form's hidden `.product-variant-id` input (core Shopify), drives
 * the displayed price, and hides the theme's duplicate variant picker. Charges
 * correctly on ANY plan — no Cart Transform / Plus.
 *
 * Visual behaviours ported from the approved demo:
 *   - live selected-value labels (data-dvl-value)
 *   - font-chip live previews
 *   - resin colour crossfades its photo into the product gallery (data-swaps-gallery)
 *   - conditional fields reveal/collapse (CSS animates; JS toggles class + disabled)
 *   - counters, file-size guard, required validation
 *
 * Defensive throughout: any failure degrades to "all fields visible/enabled".
 */
(function () {
  'use strict';

  var FONT_STACKS = {
    'Arial Black': "'Arial Black', sans-serif",
    'Bell MT': "Cambria, serif",
    'Embassy BT': "'Brush Script MT', cursive",
    'Calibri': "Calibri, sans-serif",
    'Freestyle Script': "'Segoe Script', 'Brush Script MT', cursive",
    'Segoe Print': "'Segoe Print', 'Comic Sans MS', cursive"
  };

  function init(root) {
    var product = readProduct(root);
    var moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';

    // Move the block INTO the real product form (the one with the variant-id
    // input + visible Add button) so the variant id and properties actually post.
    var form;
    try { form = hoistIntoForm(root); } catch (e) {}
    if (!form) form = root.closest('form');

    var fields = Array.prototype.slice.call(root.querySelectorAll('[data-dvl-field]'));

    try { hideNativePicker(form); } catch (e) {}
    try { applyFontChips(root); } catch (e) {}

    function evaluate() {
      var values = collectValues();
      fields.forEach(function (field) {
        var raw = field.getAttribute('data-show-when');
        if (!raw) return;
        var rule;
        try { rule = JSON.parse(decodeEntities(raw)); } catch (e) { return; }
        var current = values[rule.field];
        var show = rule.in
          ? rule.in.indexOf(current) !== -1
          : (rule.equals !== undefined ? current === rule.equals : true);
        toggleField(field, show);
      });
    }

    function syncVariant() {
      if (!product || !product.variants.length) return;
      var axisValues = {};
      var complete = true;
      fields.forEach(function (field) {
        var optName = field.getAttribute('data-variant-axis');
        if (!optName) return;
        var v = axisSelection(field);
        if (v === null || v === '') complete = false;
        else axisValues[optName] = v;
      });
      var variant = complete ? matchVariant(product, axisValues) : null;
      if (variant) {
        // update every product form's variant-id (main + sticky/duplicate forms)
        document.querySelectorAll('.product-variant-id').forEach(function (el) {
          el.value = variant.id; el.removeAttribute('disabled');
        });
        if (form) {
          var fid = form.querySelector('input[name="id"]');
          if (fid) { fid.value = variant.id; fid.removeAttribute('disabled'); }
        }
        updatePrice(form, variant.price, moneyFormat);
        updateButtonPrice(variant.price, moneyFormat);
      }
    }

    // live selected-value labels (e.g. "Resin colour · Ocean Blue")
    function updateValueLabels() {
      fields.forEach(function (field) {
        var out = field.querySelector('[data-dvl-value]');
        if (!out) return;
        var v = selectedValue(field);
        if (v) out.textContent = v;
      });
    }

    // crossfade the THIS PRODUCT'S own photo for the chosen colour into the gallery.
    // The colour is matched from the filename (e.g. ..._Blue, ..._Baby_Blue) against
    // the product's own media, so it never shows another product's ring.
    var galleryMedia = (product && product.media) || [];
    var colorTokens = buildColorTokens(root);
    function handleGallery(target) {
      var field = target && target.closest ? target.closest('[data-swaps-gallery]') : null;
      if (!field) return;
      var chosen = field.querySelector('input:checked');
      if (!chosen) return;
      var selToken = chosen.getAttribute('data-color') || tokenFromConfigImage(chosen.getAttribute('data-image') || '');
      if (!selToken) return;
      var url = null;
      for (var i = 0; i < galleryMedia.length; i++) {
        if (longestSuffixToken(galleryMedia[i], colorTokens) === selToken) { url = galleryMedia[i]; break; }
      }
      if (url) swapGalleryImage(url); // only swap to one of THIS product's own images
    }

    root.addEventListener('change', function (e) {
      evaluate(); syncVariant(); updateValueLabels(); handleGallery(e.target);
    });
    root.addEventListener('input', function () { evaluate(); });

    root.querySelectorAll('.dvl-text[maxlength]').forEach(function (input) {
      var counter = input.parentNode.querySelector('[data-counter]');
      if (!counter) return;
      var max = input.getAttribute('maxlength');
      var update = function () { counter.textContent = input.value.length + ' / ' + max; };
      input.addEventListener('input', update); update();
    });

    root.querySelectorAll('.dvl-file').forEach(function (input) {
      input.addEventListener('change', function () {
        var preview = input.parentNode.querySelector('[data-file-preview]');
        var maxMb = parseFloat(input.getAttribute('data-max-mb') || '10');
        var file = input.files && input.files[0];
        if (!file) { if (preview) preview.hidden = true; return; }
        if (file.size > maxMb * 1024 * 1024) {
          alert('That file is larger than ' + maxMb + 'MB. Please choose a smaller image.');
          input.value = ''; if (preview) preview.hidden = true; return;
        }
        if (preview) { preview.textContent = '✓ ' + file.name; preview.hidden = false; }
      });
    });

    root.querySelectorAll('[data-font-picker]').forEach(function (select) {
      select.addEventListener('change', function () {
        select.style.fontFamily = select.value ? "'" + select.value + "', serif" : '';
      });
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        var missing = firstMissingRequired(root);
        if (missing) {
          e.preventDefault(); e.stopPropagation();
          missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
          var el = missing.querySelector('input, select');
          if (el) el.focus();
          flash(missing);
        }
      }, true);
    }

    autoSelectSingles(fields);
    evaluate(); syncVariant(); updateValueLabels();
  }

  // ---- helpers --------------------------------------------------------------

  // Pre-select a field that has exactly one choice (e.g. Metal: Silver), so a
  // required single-option field doesn't force a pointless click / block submit.
  function autoSelectSingles(fields) {
    fields.forEach(function (field) {
      var radios = field.querySelectorAll('input[type="radio"]');
      if (radios.length === 1 && !radios[0].checked) radios[0].checked = true;
    });
  }

  function readProduct(root) {
    var el = (root.parentNode && root.parentNode.querySelector('[data-dvl-product]')) || document.querySelector('[data-dvl-product]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // ---- colour -> product image matching -----------------------------------
  function norm(s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); }

  // filename without path, query, extension and any trailing UUID
  function fileCore(url) {
    var m = /\/([^\/?#]+)\.(?:jpe?g|png|webp)(?:[?#]|$)/i.exec(url);
    var fn = m ? m[1] : '';
    return fn.replace(/_[0-9a-f]{8}-[0-9a-f-]{20,}$/i, '');
  }

  // colour token from a shared-config (RG_013…) image url, e.g. RG_013_S_Baby_Blue -> babyblue
  function tokenFromConfigImage(url) {
    var fn = fileCore(url).replace(/^RG[_-]?\d+[_-]?(?:S[_-])?/i, '');
    return norm(fn);
  }

  // the longest colour token that is a suffix of this image's filename
  function longestSuffixToken(url, tokens) {
    var n = norm(fileCore(url));
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (n.length >= t.length && n.slice(-t.length) === t) return t;
    }
    return null;
  }

  // build the colour-token list from the resin swatches' config images (longest first)
  function buildColorTokens(root) {
    var set = {};
    root.querySelectorAll('[data-swaps-gallery] input[data-image]').forEach(function (inp) {
      var t = tokenFromConfigImage(inp.getAttribute('data-image'));
      if (t) { inp.setAttribute('data-color', t); set[t] = 1; }
    });
    return Object.keys(set).sort(function (a, b) { return b.length - a.length; });
  }

  function applyFontChips(root) {
    root.querySelectorAll('[data-font-face]').forEach(function (face) {
      var name = face.getAttribute('data-font-face');
      if (FONT_STACKS[name]) face.style.fontFamily = FONT_STACKS[name];
    });
  }

  function axisSelection(field) {
    var sel = field.querySelector('select[data-dvl-axis-select]');
    if (sel) return sel.value;
    var checked = field.querySelector('input[data-variant-value]:checked');
    return checked ? checked.getAttribute('data-variant-value') : null;
  }

  // The chosen display value for any field (axis or property).
  function selectedValue(field) {
    if (field.getAttribute('data-variant-axis')) return axisSelection(field);
    var checked = field.querySelector('input[type="radio"]:checked');
    if (checked) return checked.value;
    var sel = field.querySelector('select');
    if (sel && sel.value) return sel.value;
    var text = field.querySelector('input[type="text"]');
    if (text && text.value) return text.value;
    return null;
  }

  function matchVariant(product, axisValues) {
    var order = product.options || [];
    var target = [];
    for (var i = 0; i < order.length; i++) {
      var val = axisValues[order[i]];
      if (val === undefined) return null;
      target.push(val);
    }
    for (var v = 0; v < product.variants.length; v++) {
      var opts = product.variants[v].options || [];
      if (opts.length !== target.length) continue;
      var same = true;
      for (var j = 0; j < target.length; j++) { if (opts[j] !== target[j]) { same = false; break; } }
      if (same) return product.variants[v];
    }
    return null;
  }

  function updatePrice(form, cents, format) {
    var scope = (form && form.closest('[id^="shopify-section"]')) || document;
    var money = formatMoney(cents, format);
    scope.querySelectorAll('.price-item--regular').forEach(function (el) { el.textContent = money; });
  }

  // Update the price shown inside the Add-to-cart button(s) (theme's .buttonPrice).
  function updateButtonPrice(cents, format) {
    var money = formatMoney(cents, format);
    document.querySelectorAll('.buttonPrice').forEach(function (el) { el.textContent = '— ' + money; });
  }

  function hideNativePicker(form) {
    var scope = (form && form.closest('[id^="shopify-section"]')) || document;
    scope.querySelectorAll('variant-selects').forEach(function (el) { el.style.display = 'none'; });
  }

  // Swap + crossfade the theme's main product gallery image.
  function swapGalleryImage(url) {
    var img =
      document.querySelector('.product__media-wrapper .splide__slide.is-active img') ||
      document.querySelector('.product__media-wrapper .splide__slide img') ||
      document.querySelector('.product__media-wrapper img') ||
      document.querySelector('.product__media img, .product-media img');
    if (!img) return;
    var pre = new Image();
    pre.onload = function () {
      img.style.transition = 'opacity .4s ease';
      img.style.opacity = '0';
      setTimeout(function () {
        img.removeAttribute('srcset');
        img.removeAttribute('data-srcset');
        img.src = url;
        img.srcset = url;
        requestAnimationFrame(function () { img.style.opacity = '1'; });
      }, 180);
    };
    pre.src = url;
  }

  // The real product form is the /cart/add form that has the variant-id input
  // and a visible Add button. This theme renders several /cart/add forms, so we
  // must pick the right one (not just the first).
  function findCartForm() {
    var forms = Array.prototype.slice.call(document.querySelectorAll('form[action*="/cart/add"]'));
    var withId = forms.filter(function (f) { return f.querySelector('.product-variant-id'); });
    var visible = withId.filter(function (f) {
      var a = f.querySelector('[name="add"]');
      return a && a.offsetParent !== null;
    });
    return visible[0] || withId[0] || forms[0] || null;
  }

  function hoistIntoForm(root) {
    var form = findCartForm();
    if (!form) return root.closest('form');
    var buttons = form.querySelector('.product-form__buttons') || form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
    if (buttons && buttons.parentNode) {
      // options block sits above the buy button
      buttons.parentNode.insertBefore(root, buttons);
      // quantity selector sits directly above the buy button (just below the options)
      var qty = document.querySelector('.product-form__quantity');
      if (qty && qty !== buttons) buttons.parentNode.insertBefore(qty, buttons);
    } else if (root.closest('form') !== form) {
      form.appendChild(root);
    }
    if (root.querySelector('.dvl-file')) form.setAttribute('enctype', 'multipart/form-data');
    return form;
  }

  function collectValues() {
    var values = {};
    document.querySelectorAll('[data-dvl-field]').forEach(function (field) {
      var key = field.getAttribute('data-field-key');
      if (!key) return;
      var v = selectedValue(field);
      if (v !== null && v !== '') values[key] = v;
    });
    return values;
  }

  function toggleField(field, show) {
    field.classList.toggle('is-hidden', !show);
    field.querySelectorAll('input, select, textarea').forEach(function (el) {
      el.disabled = !show;
      if (el.getAttribute('data-required') === 'true') el.required = show;
    });
  }

  function firstMissingRequired(root) {
    var fields = root.querySelectorAll('[data-dvl-field]:not(.is-hidden)');
    for (var i = 0; i < fields.length; i++) {
      var field = fields[i];
      if (field.getAttribute('data-variant-axis')) {
        var req = field.querySelector('[data-required="true"]');
        if (req && (axisSelection(field) === null || axisSelection(field) === '')) return field;
        continue;
      }
      var controls = field.querySelectorAll('[data-required="true"]');
      if (!controls.length) continue;
      var type = controls[0].type;
      if (type === 'radio') {
        if (!field.querySelector('input[type="radio"]:checked')) return field;
      } else if (!String(controls[0].value || '').trim() && !controls[0].disabled) {
        if (controls[0].type === 'file' && (!controls[0].files || !controls[0].files.length)) return field;
        if (controls[0].type !== 'file') return field;
      }
    }
    return null;
  }

  function flash(el) {
    el.classList.add('dvl-field--error');
    setTimeout(function () { el.classList.remove('dvl-field--error'); }, 2000);
  }

  function decodeEntities(str) {
    var t = document.createElement('textarea');
    t.innerHTML = str;
    return t.value;
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ','; decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var frac = parts[1] ? decimal + parts[1] : '';
      return whole + frac;
    }
    var match = format.match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';
    var value;
    switch (placeholder) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2);
    }
    return format.replace(placeholderRegex, value);
  }

  function boot() { document.querySelectorAll('[data-dvl-options]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
